import { isDevMode } from '@angular/core';
import { ColumnBase, ColumnComponent, GridComponent } from '@progress/kendo-angular-grid';
import { ConfigurableGridColumn } from './column-configurator.component';
import { configKeyByColumn } from './config-key.directive';

/**
 * Brings the grid's visual column order in line with `order` (configurable column keys, saved
 * preference). An empty `order` restores the template-declared order.
 *
 * Only the columns that map to a configurable key are rearranged; the rest (checkbox, command
 * columns...) keep the slots they occupy. Kendo's `orderIndex` is read-only and `grid.columns`
 * always stays in template order, so the visual order is derived from `orderIndex` and changed
 * through `reorderColumn()`. Idempotent: it only reorders what is out of place.
 */
export function applyColumnOrder(
    grid: GridComponent,
    configurableColumns: ConfigurableGridColumn[],
    order: string[],
    translate: (text: string) => string
): void {
    const declared = grid.columns.toArray().filter((column) => column.level === 0);
    const matched = matchColumns(declared, configurableColumns, translate);
    if (isDevMode()) {
        const unmatched = configurableColumns
            .filter((column) => (column.isAvailable?.() ?? true) && !matched.has(column.key))
            .map((column) => column.key);
        if (unmatched.length > 0) {
            console.warn('[column-configurator] no grid column matches these keys, they cannot be reordered:', unmatched);
        }
    }

    const lockedKeys = new Set(configurableColumns.filter((column) => column.locked).map((column) => column.key));
    const isLocked = (key: string, column: ColumnBase) => lockedKeys.has(key) || !!asColumn(column).locked;
    if (isDevMode()) {
        const unflagged = Array.from(matched.entries())
            .filter(([key, column]) => asColumn(column).locked && !lockedKeys.has(key))
            .map(([key]) => key);
        if (unflagged.length > 0) {
            console.warn(
                '[column-configurator] these keys are bound to locked grid columns but not flagged `locked: true`:',
                unflagged
            );
        }
    }

    if (matched.size < 2) {
        return;
    }

    // Locked columns lead and stay in declared order; reordering a column across the locked boundary
    // would make Kendo lock/unlock it.
    const declaredIndex = (column: ColumnBase) => declared.indexOf(column);
    const rank = (key: string, column: ColumnBase) => {
        if (isLocked(key, column)) {
            return -1;
        }
        const position = order.indexOf(key);
        return position === -1 ? Number.MAX_SAFE_INTEGER : position;
    };
    const desired = Array.from(matched.entries())
        .sort(([keyA, a], [keyB, b]) => rank(keyA, a) - rank(keyB, b) || declaredIndex(a) - declaredIndex(b))
        .map(([, column]) => column);
    const matchedColumns = new Set(desired);

    const visualOrder = () => [...declared].sort((a, b) => a.orderIndex - b.orderIndex);
    const slots = visualOrder()
        .map((column, index) => (matchedColumns.has(column) ? index : -1))
        .filter((index) => index !== -1);

    slots.forEach((slot, position) => {
        const current = visualOrder();
        if (current[slot] !== desired[position]) {
            grid.reorderColumn(desired[position], slot, { before: true });
        }
    });
}

function matchColumns(
    declared: ColumnBase[],
    configurableColumns: ConfigurableGridColumn[],
    translate: (text: string) => string
): Map<string, ColumnBase> {
    const used = new Set<ColumnBase>();
    const matched = new Map<string, ColumnBase>();

    configurableColumns.forEach((configurable) => {
        const title = translate(configurable.title);
        const isFree = (candidate: ColumnBase) => !used.has(candidate) && !configKeyByColumn.has(candidate);
        const column =
            declared.find((candidate) => !used.has(candidate) && configKeyByColumn.get(candidate) === configurable.key) ??
            declared.find((candidate) => isFree(candidate) && asColumn(candidate).field === configurable.key) ??
            declared.find((candidate) => isFree(candidate) && asColumn(candidate).title === title);

        if (column) {
            used.add(column);
            matched.set(configurable.key, column);
        }
    });

    return matched;
}

function asColumn(column: ColumnBase): Partial<ColumnComponent> {
    return column as Partial<ColumnComponent>;
}
